import React, { useState, useEffect } from 'react'
import { Plus, Trash2, Upload, Globe, Mail, Loader, AlertCircle, CheckCircle } from 'lucide-react'

interface AllowlistEntry {
  id: string
  type: 'domain' | 'email'
  value: string
  addedBy: string
  addedAt: string
  note?: string
}

const AllowlistManager = () => {
  const [allowedDomains, setAllowedDomains] = useState<AllowlistEntry[]>([])
  const [allowedEmails, setAllowedEmails] = useState<AllowlistEntry[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [bulkDomainsText, setBulkDomainsText] = useState('')
  const [bulkEmailsText, setBulkEmailsText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // Per-section feedback so the message is visible near the action
  const [domainSuccess, setDomainSuccess] = useState('')
  const [emailSuccess, setEmailSuccess] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)

  useEffect(() => {
    fetchAllowlists()
  }, [])

  const fetchAllowlists = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/admin/allowlist', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setAllowedDomains(data.domains || [])
        setAllowedEmails(data.emails || [])
      } else if (response.status === 403) {
        setAccessDenied(true)
      } else {
        setError('Failed to fetch allowlists')
      }
    } catch (err) {
      // Network errors - fail silently
      setAccessDenied(true)
    } finally {
      setLoading(false)
    }
  }

  const handleAddSingle = async (type: 'domain' | 'email', value: string) => {
    if (!value.trim()) return

    try {
      setLoading(true)
      setError('')
      setSuccess('')
      setDomainSuccess('')
      setEmailSuccess('')

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/admin/allowlist', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ type, values: value.trim() })
      })

      if (response.ok) {
        // Optimistic update: add to local state immediately so the list
        // reflects the change without waiting for Netlify Blobs consistency
        const newEntry: AllowlistEntry = {
          id: `temp-${Date.now()}`,
          type,
          value: value.trim(),
          addedBy: 'you',
          addedAt: new Date().toISOString()
        }
        if (type === 'domain') {
          setAllowedDomains(prev => [...prev, newEntry])
          setNewDomain('')
          setDomainSuccess(`Added domain: ${value.trim()}`)
        } else {
          setAllowedEmails(prev => [...prev, newEntry])
          setNewEmail('')
          setEmailSuccess(`Added email: ${value.trim()}`)
        }
        // Background refresh to sync real server IDs (don't await — prevents stale overwrite)
        fetchAllowlists()
      } else {
        const data = await response.json()
        setError(data.message || 'Failed to add entry')
      }
    } catch (err) {
      setError(`Error adding ${type}`)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkAdd = async (type: 'domain' | 'email', text: string) => {
    const values = text.split('\n').map(v => v.trim()).filter(v => v.length > 0)

    if (values.length === 0) {
      setError('No values to add')
      return
    }

    try {
      setLoading(true)
      setError('')
      setSuccess('')
      setDomainSuccess('')
      setEmailSuccess('')

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/admin/allowlist', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ type, values })
      })

      if (response.ok) {
        const data = await response.json()
        // Optimistic update for bulk adds
        const newEntries: AllowlistEntry[] = values.map((v, i) => ({
          id: `temp-${Date.now()}-${i}`,
          type,
          value: v,
          addedBy: 'you',
          addedAt: new Date().toISOString()
        }))
        if (type === 'domain') {
          setAllowedDomains(prev => [...prev, ...newEntries])
          setBulkDomainsText('')
          setDomainSuccess(`Added ${data.added} domain(s)`)
        } else {
          setAllowedEmails(prev => [...prev, ...newEntries])
          setBulkEmailsText('')
          setEmailSuccess(`Added ${data.added} email(s)`)
        }
        // Background refresh to sync real server IDs
        fetchAllowlists()
      } else {
        const data = await response.json()
        setError(data.message || 'Failed to add entries')
      }
    } catch (err) {
      setError(`Error adding ${type}s`)
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (type: 'domain' | 'email', id: string) => {
    if (!confirm(`Remove this ${type} from the allowlist?`)) return

    try {
      setLoading(true)
      setError('')
      setDomainSuccess('')
      setEmailSuccess('')

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/admin/allowlist', {
        method: 'DELETE',
        credentials: 'include',
        headers,
        body: JSON.stringify({ type, id })
      })

      if (response.ok) {
        // Optimistic remove
        if (type === 'domain') {
          setAllowedDomains(prev => prev.filter(d => d.id !== id))
          setDomainSuccess('Domain removed')
        } else {
          setAllowedEmails(prev => prev.filter(e => e.id !== id))
          setEmailSuccess('Email removed')
        }
        fetchAllowlists()
      } else {
        setError('Failed to remove entry')
      }
    } catch (err) {
      setError(`Error removing ${type}`)
    } finally {
      setLoading(false)
    }
  }

  if (accessDenied) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Access Allowlist</h2>
        <p className="mt-2 text-gray-600">
          Manage domains and email addresses that are automatically approved for trust center access.
          Any access request from an allowlisted domain or email will be auto-approved.
        </p>
      </div>

      {/* Global error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-600">{error}</span>
        </div>
      )}

      {/* Domains Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Globe className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Allowed Domains</h3>
        </div>

        {/* Inline domain success */}
        {domainSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
            <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-700 text-sm">{domainSuccess}</span>
          </div>
        )}

        {/* Add Single Domain */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Add Domain</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && handleAddSingle('domain', newDomain)}
            />
            <button
              onClick={() => handleAddSingle('domain', newDomain)}
              disabled={loading || !newDomain.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </button>
          </div>
        </div>

        {/* Bulk Add Domains */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Add Domains (one per line)</label>
          <textarea
            value={bulkDomainsText}
            onChange={(e) => setBulkDomainsText(e.target.value)}
            placeholder="example.com&#10;another-domain.com&#10;trusted-partner.org"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={4}
          />
          <button
            onClick={() => handleBulkAdd('domain', bulkDomainsText)}
            disabled={loading || !bulkDomainsText.trim()}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Upload className="h-4 w-4 mr-1" />
            Bulk Add Domains
          </button>
        </div>

        {/* Domain List */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Current Domains ({allowedDomains.length})
          </h4>
          {allowedDomains.length === 0 ? (
            <p className="text-gray-500 text-sm">No domains in allowlist</p>
          ) : (
            <div className="space-y-2">
              {allowedDomains.map((domain) => (
                <div key={domain.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{domain.value}</p>
                    <p className="text-xs text-gray-500">
                      Added by {domain.addedBy} on {new Date(domain.addedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove('domain', domain.id)}
                    disabled={loading}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Emails Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Mail className="h-6 w-6 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Allowed Email Addresses</h3>
        </div>

        {/* Inline email success */}
        {emailSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
            <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-700 text-sm">{emailSuccess}</span>
          </div>
        )}

        {/* Add Single Email */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Add Email</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              onKeyPress={(e) => e.key === 'Enter' && handleAddSingle('email', newEmail)}
            />
            <button
              onClick={() => handleAddSingle('email', newEmail)}
              disabled={loading || !newEmail.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </button>
          </div>
        </div>

        {/* Bulk Add Emails */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Add Emails (one per line)</label>
          <textarea
            value={bulkEmailsText}
            onChange={(e) => setBulkEmailsText(e.target.value)}
            placeholder="user1@example.com&#10;user2@example.com&#10;partner@company.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            rows={4}
          />
          <button
            onClick={() => handleBulkAdd('email', bulkEmailsText)}
            disabled={loading || !bulkEmailsText.trim()}
            className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
          >
            <Upload className="h-4 w-4 mr-1" />
            Bulk Add Emails
          </button>
        </div>

        {/* Email List */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Current Emails ({allowedEmails.length})
          </h4>
          {allowedEmails.length === 0 ? (
            <p className="text-gray-500 text-sm">No emails in allowlist</p>
          ) : (
            <div className="space-y-2">
              {allowedEmails.map((email) => (
                <div key={email.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{email.value}</p>
                    <p className="text-xs text-gray-500">
                      Added by {email.addedBy} on {new Date(email.addedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove('email', email.id)}
                    disabled={loading}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      )}
    </div>
  )
}

export default AllowlistManager
